import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiImage } from "react-icons/fi";
import { galleryAPI } from "../../services/api";
import { resolveImageUrl } from "../../utils/helpers";
import { useDataRefresh } from "../../hooks/useDataRefresh";
import useScrollAnimation from "../../hooks/useScrollAnimation";

const fallbackImages = [
  { _id: "1", title: "Annual Sports Day", category: "Sports" },
  { _id: "2", title: "Science Exhibition", category: "Academic" },
  { _id: "3", title: "Cultural Program", category: "Events" },
  { _id: "4", title: "Graduation Ceremony", category: "Events" },
  { _id: "5", title: "Campus Tour", category: "Campus" },
  { _id: "6", title: "Library Session", category: "Academic" },
];

const gradients = [
  "from-primary-500 to-primary-800",
  "from-secondary-500 to-secondary-700",
  "from-primary-600 to-primary-900",
  "from-secondary-400 to-primary-600",
  "from-primary-400 to-primary-700",
  "from-secondary-600 to-primary-700",
];

export default function GalleryPreview() {
  const [imgErrors, setImgErrors] = useState({});
  const { ref, isVisible } = useScrollAnimation();

  const { data: images = [] } = useDataRefresh(
    async () => {
      try {
        // Fetch many images to ensure we have at least one from each category
        const res = await galleryAPI.getAll({
          limit: 1000,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        const allImages = res.data?.data?.images || [];

        if (allImages.length === 0) {
          return fallbackImages;
        }

        // Get one image from each category
        const categories = [
          "Campus",
          "Events",
          "Sports",
          "Academic",
          "Cultural",
          "Other",
        ];
        const selectedImages = [];
        const usedCategories = new Set();

        // First pass: get one image from each category
        for (const image of allImages) {
          if (usedCategories.size === categories.length) break;
          if (
            categories.includes(image.category) &&
            !usedCategories.has(image.category)
          ) {
            selectedImages.push(image);
            usedCategories.add(image.category);
          }
        }

        // If we have less than 6 images, fill with remaining images
        if (selectedImages.length < 6) {
          for (const image of allImages) {
            if (selectedImages.length >= 6) break;
            if (!selectedImages.includes(image)) {
              selectedImages.push(image);
            }
          }
        }

        return selectedImages.slice(0, 6);
      } catch {
        return fallbackImages;
      }
    },
    "gallery",
    { pollInterval: 0 }, // No auto-refresh
  );

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 sm:mb-12"
        >
          <div>
            <p className="section-subtitle mb-2">Our Memories</p>
            <h2 className="section-title">Gallery</h2>
          </div>
          <Link
            to="/gallery"
            className="mt-3 sm:mt-0 inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-secondary-600 transition-colors text-sm sm:text-base"
          >
            View Full Gallery <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {images.map((image, idx) => (
            <motion.div
              key={image._id || idx}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="relative overflow-hidden rounded-xl sm:rounded-2xl group cursor-pointer shadow-sm h-36 sm:h-48 md:h-56 lg:h-60"
            >
              {image.imageUrl && !imgErrors[image._id] ? (
                <img
                  src={resolveImageUrl(image.imageUrl)}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={() =>
                    setImgErrors((prev) => ({ ...prev, [image._id]: true }))
                  }
                />
              ) : (
                <div
                  className={`w-full h-full bg-gradient-to-br ${gradients[idx % gradients.length]} flex items-center justify-center`}
                >
                  <FiImage className="w-12 h-12 text-white/30" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div>
                  <p className="text-white font-semibold text-sm">
                    {image.title}
                  </p>
                  {image.category && (
                    <span className="text-white/60 text-xs">
                      {image.category}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/25"
          >
            <FiImage className="w-5 h-5" />
            View Full Gallery
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
